/* =========================================================================
   ClueVine — the one ad
   A single AdSense banner shown to non-supporters when the owner has enabled
   ads (and set a client + slot) in the admin area. Supporters always get it
   removed. In dev, an unconfigured/disabled slot shows a labelled placeholder.
   ========================================================================= */

import { useEffect, useRef } from 'react';
import { useSiteConfig } from '../state/SiteConfigContext';
import { useSupporter } from '../state/SupporterContext';

let scriptInjected = false;
function ensureAdScript(client: string) {
  if (scriptInjected || typeof document === 'undefined' || !client) return;
  scriptInjected = true;
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${client}`;
  s.crossOrigin = 'anonymous';
  document.head.appendChild(s);
}

export function AdSlot() {
  const { isSupporter } = useSupporter();
  const { config } = useSiteConfig();
  const pushed = useRef(false);
  const configured = Boolean(config.adsEnabled && config.adsClient && config.adsSlot);

  useEffect(() => {
    if (isSupporter || !configured || pushed.current) return;
    ensureAdScript(config.adsClient);
    pushed.current = true;
    try {
      const w = window as unknown as { adsbygoogle?: Array<Record<string, unknown>> };
      w.adsbygoogle = w.adsbygoogle ?? [];
      w.adsbygoogle.push({});
    } catch {
      /* ad blocker / not approved yet — fine */
    }
  }, [isSupporter, configured, config.adsClient]);

  if (isSupporter) return null;

  if (!configured) {
    if (import.meta.env.DEV) {
      return (
        <div className="cv-ad cv-ad--placeholder">
          Ad slot — enable + set client/slot in the admin area. Supporters don’t see this.
        </div>
      );
    }
    return null;
  }

  return (
    <div className="cv-ad" aria-label="Advertisement">
      <ins
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client={config.adsClient}
        data-ad-slot={config.adsSlot}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  );
}
